import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { STELLEN_STATUS, EINRICHTUNGSTYPEN, BUNDESLAENDER, QUALIFIKATIONEN, ARBEITSBEREICHE, SCHICHTWUENSCHE } from '../../lib/constants'
import type { Stelle } from '../../lib/types'
import './AdminPages.css'

const EMPTY_FORM = {
  titel: '', ort: '', bundesland: '', plz: '', einrichtungstyp: '',
  arbeitsmodell: '', einsatzbereich: '', erforderliche_qualifikation: '',
  schichtmodell: '', nacht_erforderlich: false,
  gehalt_min: '', gehalt_max: '', beschreibung: '',
}

export default function StellenListe() {
  const [stellen, setStellen] = useState<Stelle[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterBundesland, setFilterBundesland] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({ ...EMPTY_FORM })
  const [saving, setSaving] = useState(false)
  const navigate = useNavigate()

  useEffect(() => { loadData() }, [filterStatus, filterBundesland])

  async function loadData() {
    setLoading(true)
    let query = supabase.from('stellen').select('*, arbeitgeber(name)').order('created_at', { ascending: false })
    if (filterStatus) query = query.eq('status', filterStatus)
    if (filterBundesland) query = query.eq('bundesland', filterBundesland)
    const { data } = await query
    setStellen((data as Stelle[]) || [])
    setLoading(false)
  }

  async function handleCreate() {
    if (!formData.titel) return
    setSaving(true)
    const payload: any = {
      ...formData,
      quelle: 'manuell',
      gehalt_min: formData.gehalt_min ? parseInt(formData.gehalt_min) : null,
      gehalt_max: formData.gehalt_max ? parseInt(formData.gehalt_max) : null,
      nacht_erforderlich: formData.nacht_erforderlich,
    }
    // Remove empty strings
    Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null })
    const { error } = await supabase.from('stellen').insert(payload)
    if (!error) {
      setShowForm(false)
      setFormData({ ...EMPTY_FORM })
      loadData()
    } else {
      alert('Fehler: ' + error.message)
    }
    setSaving(false)
  }

  const filtered = stellen.filter((s) => {
    if (!search) return true
    const q = search.toLowerCase()
    return s.titel?.toLowerCase().includes(q) || s.ort?.toLowerCase().includes(q) || s.plz?.includes(q)
  })

  return (
    <div className="admin-page animate-fade-in-up">
      <div className="page-header">
        <div>
          <h1>Stellen</h1>
          <p className="page-subtitle">{stellen.length} Stellen gesamt</p>
        </div>
        <button className="btn btn--primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Abbrechen' : '+ Neue Stelle'}
        </button>
      </div>

      {showForm && (
        <div className="detail-section animate-fade-in-up" style={{ marginBottom: 'var(--space-6)' }}>
          <h3>Neue Stelle anlegen</h3>
          <div className="form-grid">
            <div className="form-field"><label>Titel *</label><input value={formData.titel} onChange={(e) => setFormData({ ...formData, titel: e.target.value })} placeholder="z.B. Pflegefachkraft (m/w/d)" id="stelle-titel" /></div>
            <div className="form-field"><label>Ort</label><input value={formData.ort} onChange={(e) => setFormData({ ...formData, ort: e.target.value })} placeholder="Ort" id="stelle-ort" /></div>
            <div className="form-field"><label>PLZ</label><input value={formData.plz} onChange={(e) => setFormData({ ...formData, plz: e.target.value })} placeholder="PLZ" id="stelle-plz" /></div>
            <div className="form-field">
              <label>Bundesland</label>
              <select value={formData.bundesland} onChange={(e) => setFormData({ ...formData, bundesland: e.target.value })} id="stelle-bl">
                <option value="">Auswählen...</option>
                {BUNDESLAENDER.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Einrichtungstyp</label>
              <select value={formData.einrichtungstyp} onChange={(e) => setFormData({ ...formData, einrichtungstyp: e.target.value })} id="stelle-typ">
                <option value="">Auswählen...</option>
                {EINRICHTUNGSTYPEN.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Einsatzbereich</label>
              <select value={formData.einsatzbereich} onChange={(e) => setFormData({ ...formData, einsatzbereich: e.target.value })} id="stelle-bereich">
                <option value="">Auswählen...</option>
                {ARBEITSBEREICHE.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Erforderliche Qualifikation</label>
              <select value={formData.erforderliche_qualifikation} onChange={(e) => setFormData({ ...formData, erforderliche_qualifikation: e.target.value })} id="stelle-qual">
                <option value="">Auswählen...</option>
                {QUALIFIKATIONEN.map((q) => <option key={q.value} value={q.value}>{q.label}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Arbeitsmodell</label>
              <select value={formData.arbeitsmodell} onChange={(e) => setFormData({ ...formData, arbeitsmodell: e.target.value })} id="stelle-modell">
                <option value="">Auswählen...</option>
                <option value="vollzeit">Vollzeit</option>
                <option value="teilzeit">Teilzeit</option>
                <option value="minijob">Minijob</option>
              </select>
            </div>
            <div className="form-field">
              <label>Schichtmodell</label>
              <select value={formData.schichtmodell} onChange={(e) => setFormData({ ...formData, schichtmodell: e.target.value })} id="stelle-schicht">
                <option value="">Auswählen...</option>
                {SCHICHTWUENSCHE.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-field"><label>Gehalt Min (€ brutto)</label><input type="number" value={formData.gehalt_min} onChange={(e) => setFormData({ ...formData, gehalt_min: e.target.value })} placeholder="z.B. 3200" id="stelle-gehalt-min" /></div>
            <div className="form-field"><label>Gehalt Max (€ brutto)</label><input type="number" value={formData.gehalt_max} onChange={(e) => setFormData({ ...formData, gehalt_max: e.target.value })} placeholder="z.B. 4000" id="stelle-gehalt-max" /></div>
            <div className="form-field" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" checked={formData.nacht_erforderlich} onChange={(e) => setFormData({ ...formData, nacht_erforderlich: e.target.checked })} id="stelle-nacht" style={{ width: '18px', height: '18px' }} />
              <label htmlFor="stelle-nacht" style={{ margin: 0 }}>Nachtdienst erforderlich</label>
            </div>
          </div>
          <div className="form-field" style={{ marginTop: 'var(--space-4)' }}>
            <label>Beschreibung</label>
            <textarea value={formData.beschreibung} onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })} placeholder="Details zur Stelle..." rows={3} style={{ width: '100%', padding: '12px', border: '1.5px solid var(--color-gray-200)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', resize: 'vertical' }} id="stelle-beschreibung" />
          </div>
          <div style={{ marginTop: 'var(--space-4)' }}>
            <button className="btn btn--primary" onClick={handleCreate} disabled={saving || !formData.titel}>{saving ? 'Speichern...' : 'Stelle anlegen'}</button>
          </div>
        </div>
      )}

      <div className="filters-bar">
        <div className="search-wrapper">
          <span className="search-icon">🔍</span>
          <input type="text" placeholder="Stellen suchen..." value={search} onChange={(e) => setSearch(e.target.value)} className="search-input" id="stellen-search" />
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select" id="stellen-filter-status">
          <option value="">Alle Status</option>
          {STELLEN_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filterBundesland} onChange={(e) => setFilterBundesland(e.target.value)} className="filter-select" id="stellen-filter-bl">
          <option value="">Alle Bundesländer</option>
          {BUNDESLAENDER.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
      </div>

      <div className="table-card">
        {loading ? (
          <div className="table-loading"><div className="loading-spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><span className="empty-icon">📋</span><p>Keine Stellen gefunden.</p></div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr><th>Titel</th><th>Ort</th><th>Bundesland</th><th>Typ</th><th>Status</th><th>Erstellt</th></tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} onClick={() => navigate(`/admin/stellen/${s.id}`)}>
                    <td className="cell-name"><span>{s.titel}</span>{s.arbeitgeber && <span className="cell-sub">{(s.arbeitgeber as unknown as { name: string }).name}</span>}</td>
                    <td>{s.ort || '–'}</td>
                    <td>{BUNDESLAENDER.find((b) => b.value === s.bundesland)?.label || s.bundesland || '–'}</td>
                    <td>{s.einrichtungstyp || '–'}</td>
                    <td><span className="status-badge" style={{ '--badge-color': STELLEN_STATUS.find((st) => st.value === s.status)?.color || '#94A3B8' } as React.CSSProperties}>{STELLEN_STATUS.find((st) => st.value === s.status)?.label || s.status}</span></td>
                    <td className="cell-date">{new Date(s.created_at).toLocaleDateString('de-DE')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
